<template>
  <div class="references-wrapper" @click="$emit('select')">
    <LineButton
      v-if="showAddNewButton"
      :tooltip="t('author.addNewReference')"
      @click-btn="$emit('add-new-reference', index)"
    />
    <div
      class="list-item draggable-wrapper"
      :class="{
        'is-selected': isEditable && isSelected,
      }"
      :data-index="index"
      :draggable="isEditable"
      @dragend="handleDragEnd"
      @dragstart="handleDragStart"
    >
      <CustomReferenceListItem
        v-if="reference.addedByUser"
        :title="customTitle"
        :is-editable-title="isEditableTitle"
        :display-icon="isEditable && isSelected"
        @cancel="cancelEdit"
        @set-editable="isEditableTitle = true"
        @update="(value) => updateCustomTitle(reference.id, value)"
      />

      <template v-else>
        <InfoTooltipContent
          class="thumbnail-container"
          :id="reference.externalId"
          :title="reference.name"
          :page="page"
          :snippet="snippet"
          :thumb-url="getThumbUrl(reference)"
        />
        <div class="footnote-details">
          <div v-if="reference.customFootnote" class="title-with-custom" :class="{ 'margin-top-3': isSelected }">
            <div class="title-with-custom__ai">
              <TextBody i18n-key="author.aiOriginal" />
              <br />
              {{ reference.footnoteString }}
            </div>
            <div class="title-with-custom__custom">
              <TextBody i18n-key="common.current" />
              <br />
              <EditableField
                :value="customTitle"
                v-model="customTitle"
                :isEditing="isEditableTitle"
                :displayEditingIcon="isEditable && isSelected"
                :ellipsis="isSelected"
                @cancel="cancelEdit"
                @set-editable="isEditableTitle = true"
                @update="(value) => updateCustomTitle(reference.id, value)"
              />
            </div>
          </div>
          <div v-else>
            <EditableField
              :value="title"
              v-model="title"
              :isEditing="isEditableTitle"
              :displayEditingIcon="isEditable && isSelected"
              :ellipsis="isSelected"
              :multiline="true"
              @cancel="cancelEdit"
              @set-editable="isEditableTitle = true"
              @update="(value) => updateTitle(reference.id, value)"
            />
          </div>
        </div>
      </template>

      <DocumentEditorActions
        v-if="isEditable && isSelected && !isEditableTitle"
        @move-up="$emit('move-up')"
        @move-down="$emit('move-down')"
        @remove="confirmDeletePopupOpened = true"
      />

      <WarningPopup
        :popup-opened="confirmDeletePopupOpened"
        title-key="common.delete"
        warning-message-key="author.warningDeleteReference"
        confirm-text-key="modals.confirmDeleteText"
        @update:open="confirmDeletePopupOpened = false"
        @execute:action="removeReference"
      />
    </div>

    <LineButton
      v-if="showAddNewButton"
      :tooltip="t('author.addNewReference')"
      @click-btn="$emit('add-new-reference', index + 1)"
    />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { footnoteParsedData } from '~/utils/footnotes';
import InfoTooltipContent from '~/components/conversation/chatMessage/InfoTooltipContent.vue';
import DocumentEditorActions from '~/components/conversation/authoring/DocumentEditorActions.vue';
import TextBody from '~/components/common/TextBody.vue';
import EditableField from '~/components/common/EditableField.vue';
import WarningPopup from '~/components/common/WarningPopup.vue';
import LineButton from '~/components/common/LineButton.vue';
import CustomReferenceListItem from '~/components/common/references/CustomReferenceListItem.vue';

export default {
  components: {
    CustomReferenceListItem,
    LineButton,
    WarningPopup,
    EditableField,
    TextBody,
    DocumentEditorActions,
    InfoTooltipContent,
  },
  emits: ['select', 'move-up', 'move-down', 'remove', 'update', 'add-new-reference'],
  props: {
    index: {
      type: Number,
      required: true,
    },
    reference: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isDragging: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { page, snippet } = footnoteParsedData(props.reference);
    const showAddNewButton = computed(() => props.isEditable && props.isSelected && !props.isDragging);
    const title = computed({
      get: () => props.reference.footnoteString,
      set: (value) => {
        emit('update', value);
        isEditableTitle.value = false;
      },
    });
    const customTitle = computed({
      get: () => props.reference.customFootnote,
      set: (value) => {
        emit('update', value);
        isEditableTitle.value = false;
      },
    });
    const isEditableTitle = ref(false);
    const confirmDeletePopupOpened = ref(false);

    const cancelEdit = () => {
      isEditableTitle.value = false;
    };

    const updateTitle = async (id, value) => {
      emit('update', value);
      isEditableTitle.value = false;
    };

    const updateCustomTitle = async (id, value) => {
      emit('update', value);
      isEditableTitle.value = false;
    };

    const handleDragStart = (event) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');
      event.target.classList.add('dragging-item');
    };

    const handleDragEnd = (event) => {
      event.target.classList.remove('dragging-item');
      event.target.style.cursor = 'pointer';
    };

    const removeReference = (event) => {
      emit('remove', event);
      confirmDeletePopupOpened.value = false;
    };

    const getThumbUrl = (reference) => {
      if (!reference?.pages || !reference?.pageNumber || !reference.pageNumber[0]) {
        return '';
      }
      return reference.pages[reference.pageNumber[0]]?.small || '';
    };

    return {
      handleDragStart,
      handleDragEnd,
      page,
      snippet,
      getThumbUrl,
      title,
      isEditableTitle,
      updateTitle,
      cancelEdit,
      confirmDeletePopupOpened,
      removeReference,
      updateCustomTitle,
      customTitle,
      showAddNewButton,
      t,
    };
  },
};
</script>

<style lang="scss">
.references-wrapper {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
  background: $bb-neutral-0;

  .thumbnail-container {
    border-radius: 0;
    box-shadow: none;
    border: unset;
    border-right: 1px solid #ddd;
    width: 256px;
    min-height: 72px;
    display: flex;

    .image-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      width: 64px;
      height: 50px;
      align-items: flex-start;
      gap: $bb-spacing-1;
      border: 5px solid $bb-neutral-2;
      margin: auto;

      .default-thumb {
        margin: auto;
      }
    }
  }

  .list-item {
    @include bb-medium-text-styles('xs');
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;
    background-color: $bb-neutral-0;
    color: $bb-neutral-1;
    position: relative;
    cursor: pointer;
    user-select: none;
    border: 1px solid #ddd;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);

    &:hover {
      border: 2px solid $bb-neutral-4;
    }

    &.is-selected {
      border: 1px solid $bb-primary-3;
    }

    .footnote-details {
      width: 300px;

      .margin-top-3 {
        margin-top: $bb-spacing-3;
      }

      .title-with-custom {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: $bb-spacing-2;

        &__ai {
          color: $bb-neutral-3;
        }

        &__custom {
          overflow: hidden;
          word-break: break-all;
          white-space: nowrap;
          text-overflow: ellipsis;
          width: 100%;
        }
      }
    }
  }
}

.draggable-wrapper {
  &:active {
    cursor: -webkit-grabbing;
    cursor: -moz-grabbing;
    cursor: grabbing;
  }

  &.dragging-item {
    border: 2px solid $bb-primary-1;
    background-color: $bb-neutral-0 !important;

    .delete-button {
      display: none;
    }
  }
}
</style>
